// React, Next
import { ReactElement, useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Icons
import SpeedIcon from '@mui/icons-material/Speed'
import TimelineIcon from '@mui/icons-material/Timeline'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import ImageCarrousel from '@/components/carrousel/ImageCarrousel'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img - reused from the AIR-1000 product page
import MobileApp from 'public/img/products/Air1000/dathascy_mobile_app.webp'
import VocMap from 'public/img/products/Air1000/voc-map.webp'
import Co2Chart from 'public/img/products/Air1000/co2-chart.png'
import MapOverview from 'public/img/products/Air1000/map-overview.png'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBodySmall } from '@/assets/theme/fonts'

export default function DashboardSection() {
  const { t } = useTranslation('solutionsIotAirQualityMonitoring')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const iconSx = { fontSize: '1.8rem', color: themeColors.bg.start }

  const features: { icon: ReactElement; title: string; description: string }[] =
    [
      {
        icon: <SpeedIcon sx={iconSx} />,
        title: t('dashboard.items.01.title'),
        description: t('dashboard.items.01.description')
      },
      {
        icon: <TimelineIcon sx={iconSx} />,
        title: t('dashboard.items.02.title'),
        description: t('dashboard.items.02.description')
      },
      {
        icon: <DashboardCustomizeIcon sx={iconSx} />,
        title: t('dashboard.items.03.title'),
        description: t('dashboard.items.03.description')
      },
      {
        icon: <LocationOnIcon sx={iconSx} />,
        title: t('dashboard.items.04.title'),
        description: t('dashboard.items.04.description')
      },
      {
        icon: <FileDownloadIcon sx={iconSx} />,
        title: t('dashboard.items.05.title'),
        description: t('dashboard.items.05.description')
      },
      {
        icon: <PictureAsPdfIcon sx={iconSx} />,
        title: t('dashboard.items.06.title'),
        description: t('dashboard.items.06.description')
      },
      {
        icon: <PhoneIphoneIcon sx={iconSx} />,
        title: t('dashboard.items.07.title'),
        description: t('dashboard.items.07.description')
      }
    ]

  const stacked = w < breakpoints.desktopToMobile

  // Two columns rather than three: the carousel now takes the right rail.
  const getGrid = () => {
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <SectionHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.title')}
      />

      {/* Cards on the left, screenshots of the platform on the right. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 28rem)',
          gap: stacked ? '2.5rem' : '3rem',
          alignItems: 'start'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: getGrid(),
            gap: '1.5rem',
            alignItems: 'stretch'
          }}
        >
          {features.map(feature => (
            <Box
              key={feature.title}
              sx={{
                height: '100%',
                background: themeColors.section.light,
                border: `1px solid ${themeColors.gray.lighter}`,
                borderRadius: '10px',
                padding: '1.5rem',
                boxSizing: 'border-box'
              }}
            >
              <Box sx={{ lineHeight: 0, marginBottom: '1rem' }}>
                {feature.icon}
              </Box>

              <Typography
                variant="h3"
                component="h3"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: themeColors.kroemtech.dunkelblau,
                  marginBottom: '0.6rem'
                }}
              >
                {feature.title}
              </Typography>

              <Typography
                component="p"
                sx={{
                  fontSize: getFontBodySmall(),
                  lineHeight: 1.65,
                  color: themeColors.gray.defaultText
                }}
              >
                {feature.description}
              </Typography>
            </Box>
          ))}
        </Box>

        <ImageCarrousel
          images={[MobileApp, VocMap, Co2Chart, MapOverview]}
          alts={[
            'Air quality readings in the mobile app',
            'Floor plan map coloured by VOC level',
            'CO₂ concentration chart over time',
            'Overview map of all monitored locations'
          ]}
          ratio="4 / 3"
        />
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
